import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ShoppingCart } from 'lucide-react'
import apiClient from '@/api/client'
import type { FromSuggestionsResult } from '@/types/kitchenFlowPurchasing'
import { Button } from '@/components/ui/button'
import { fromSuggestionsOutcome, keyForSignature, shortfallSignature } from './purchasingLogic'
import { PURCHASING_STRINGS } from './purchasingStrings'

const S = PURCHASING_STRINGS.shortfall

// One key per requisition + shortfall content, kept across remounts (module
// scope), so a double tap, a retry or reopening the request never makes the
// orders twice. The server also nets orders already raised for the request
// out of the shortfall, so after a reload the list is already smaller.
const shortfallKeys = new Map<string, string>()

/** Kitchen flow lane D ↔ lane C hand-off: "Buy the shortfall" for a kitchen
 *  requisition. Reads lane C's GET /inventory-flow/requisitions/:id/shortfall
 *  (lines grouped by vendor, fetched fresh at the tap) and turns it into
 *  draft POs by vendor, each linked back to the requisition (the PO shows
 *  "for REQ-…"). */
export function BuyShortfallButton({ requisitionId, onDone, showToast }: {
  requisitionId: string
  onDone?: (r: FromSuggestionsResult | null) => void
  showToast: (t: 'success' | 'error', m: string) => void
}) {
  const qc = useQueryClient()
  const buy = useMutation({
    mutationFn: async () => {
      const groups = (await apiClient.getRequisitionShortfall(requisitionId)).data?.suppliers ?? []
      const lines = groups.flatMap((g) =>
        g.lines
          .filter((l) => l.shortfall_qty > 0)
          .map((l) => ({ stock_item_id: l.stock_item_id, qty: l.shortfall_qty, supplier_id: g.supplier_id ?? undefined })),
      )
      if (lines.length === 0) return { kind: 'nothing' as const }
      const key = keyForSignature(shortfallKeys, shortfallSignature(requisitionId, lines), () => crypto.randomUUID())
      const res = await apiClient.createPurchaseOrdersFromSuggestions({ lines, requisition_id: requisitionId, idempotency_key: key })
      return fromSuggestionsOutcome(res.data)
    },
    onSuccess: (out) => {
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      qc.invalidateQueries({ queryKey: ['orderSuggestions'] })
      qc.invalidateQueries({ queryKey: ['inventory-flow', 'shortfall', requisitionId] })
      if (out.kind === 'nothing') {
        showToast('success', S.nothingLeft)
        onDone?.(null)
      } else if (out.kind === 'replay') {
        showToast('success', S.alreadyMade)
        onDone?.(null)
      } else {
        const r = out.result
        showToast('success', S.made(r.purchase_orders.length, r.unassigned.length))
        onDone?.(r)
      }
    },
    onError: (e: Error) => showToast('error', e.message),
  })
  return (
    <Button disabled={buy.isPending} onClick={() => buy.mutate()}>
      <ShoppingCart className="mr-2 h-4 w-4" /> {buy.isPending ? S.making : S.button}
    </Button>
  )
}
